'use client';

import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  Modal,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Button
} from '@paction-2.0/ui';

import {
  getMedia,
  type MediaItem as ApiMediaItem
} from '@/lib/api/provider/media';
import { queryKeys } from '@/lib/query';

import styles from './mediaLibraryPicker.module.scss';

type MediaLibraryItem = {
  src: string;
  type: 'image' | 'video' | 'pdf';
  alt?: string;
  thumbnail?: string | null;
};

type MediaLibraryPickerProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (items: MediaLibraryItem[]) => void;
  maxSelectable?: number;
  excludeSrcs?: string[];
};

const PAGE_SIZE = 48;

export function MediaLibraryPicker({
  isOpen,
  onClose,
  onSelect,
  maxSelectable,
  excludeSrcs
}: MediaLibraryPickerProps) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [page, setPage] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: [...queryKeys.provider.media.list(), page],
    queryFn: () => getMedia({ limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
    enabled: isOpen
  });

  const items = useMemo(() => {
    if (!data?.items) return [];
    const excludeSet = new Set(excludeSrcs ?? []);
    return data.items.filter(
      (item) => item.type === 'image' && !excludeSet.has(item.src)
    );
  }, [data?.items, excludeSrcs]);

  const totalPages = data ? Math.ceil(data.pagination.total / PAGE_SIZE) : 1;

  const toggleSelection = useCallback(
    (item: ApiMediaItem) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(item.id)) {
          next.delete(item.id);
        } else {
          if (maxSelectable && next.size >= maxSelectable) return prev;
          next.add(item.id);
        }
        return next;
      });
    },
    [maxSelectable]
  );

  const handleConfirm = useCallback(() => {
    const selected = items
      .filter((item) => selectedIds.has(item.id))
      .map((item) => ({
        src: item.src,
        type: (item.type ?? 'image') as 'image' | 'video' | 'pdf',
        alt: item.alt ?? '',
        thumbnail: item.thumbnail
      }));
    onSelect(selected);
    setSelectedIds(new Set());
    setPage(0);
    onClose();
  }, [items, selectedIds, onSelect, onClose]);

  const handleClose = useCallback(() => {
    setSelectedIds(new Set());
    setPage(0);
    onClose();
  }, [onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      labelledBy="media-library-title"
    >
      <DialogHeader id="media-library-title" title="Choose from your photos" />
      <DialogBody>
        {isLoading ? (
          <p className={styles.placeholder}>Loading your media library…</p>
        ) : items.length === 0 ? (
          <p className={styles.placeholder}>
            No photos in your library yet. Photos you upload to listings,
            waitlists, breeding dogs, or conversations will appear here.
          </p>
        ) : (
          <>
            <div className={styles.grid}>
              {items.map((item) => {
                const isSelected = selectedIds.has(item.id);
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`${styles.tile} ${isSelected ? styles['tile--selected'] : ''}`}
                    onClick={() => toggleSelection(item)}
                    aria-pressed={isSelected}
                    aria-label={item.alt || item.name || 'Photo'}
                  >
                    <img
                      src={item.thumbnail || item.src}
                      alt={item.alt || item.name || ''}
                      className={styles.tileImage}
                      loading="lazy"
                    />
                    {isSelected && (
                      <div className={styles.tileCheck}>
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  type="button"
                  className={styles.pageBtn}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  ‹ Prev
                </button>
                <span className={styles.pageInfo}>
                  {page + 1} / {totalPages}
                </span>
                <button
                  type="button"
                  className={styles.pageBtn}
                  onClick={() =>
                    setPage((p) => Math.min(totalPages - 1, p + 1))
                  }
                  disabled={page >= totalPages - 1}
                >
                  Next ›
                </button>
              </div>
            )}
          </>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant="tertiary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={selectedIds.size === 0}
        >
          {selectedIds.size === 0
            ? 'Select photos'
            : `Add ${selectedIds.size} photo${selectedIds.size > 1 ? 's' : ''}`}
        </Button>
      </DialogFooter>
    </Modal>
  );
}
